import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, take } from 'rxjs';
import {
  PlaceOrderObj,
  POChoices,
  POMenuItem,
  POMenuVariations,
} from '../../interfaces/placeOrderMenu.interface';
import {
  MenuVariations,
  MenuItem,
  ChoiceGroups,
  Choices,
} from '../../interfaces/restaurantMenu.interface';
import { SharedService } from '../shared.service';
import { environment } from '../../../../environments/environment.development';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private placeOrderSubject = new BehaviorSubject<any>(null);
  public placeOrder$: Observable<any> = this.placeOrderSubject.asObservable();
  constructor(private sharedS: SharedService) { }
  updatePlaceOrder(data: any) {
    this.placeOrderSubject.next(data);
  }

  getPlaceOrderObj(): Observable<PlaceOrderObj | null> {
    return this.placeOrderSubject.asObservable();
  }

  // ===========================================================================
  // ===============  Place Order Common Function  =============================
  // ===========================================================================

  //Update Place Order Object if Empty
  // ----------------------------
  getLocalStorageData() {
    const storedData = sessionStorage.getItem(
      `sharedData@${environment.appName}`
    );
    if (storedData) {
      return JSON.parse(storedData); // Return the parsed data
    }
    return null;
  }

  updatePlaceOrderObj() {
    let localStorageData = this.getLocalStorageData();

    let newPlaceOrderObj: PlaceOrderObj = {
      // restaurant_branch_id: localStorageData.user.restaurant_branch_id,
      id: NaN,
      customer_id: null,
      notes: '',
      discount_amount: '0',
      discount_per: '0',
      tax_amount: '0',
      tax_percent: localStorageData?.user?.tax_percent ?? '16.00',
      tax_include: localStorageData?.user?.tax_include ?? false,
      delivery_charge: '0',
      service_charge: '0',
      total: '0',
      sub_total: '0',
      amount_paid: '0',
      foodpanda_commission_amount: '0',
      foodpanda_commission_percent: '0',
      amount_return: '0',
      status_id: 3,
      tax_invoice: 0,
      order_type_id: localStorageData?.user?.rolename === 'POS Call Center' ? 3 : NaN,
      payment_type_id: 1,
      time_frame_id: 2,
      order_details: [],
      // orderTableIds: [],
      order_branch_tables: [],
      orderTableName: '',
      delivery_address_id: null,
      is_pra_invoice: 0,
    };

    this.updatePlaceOrder(newPlaceOrderObj);
  }

  //Update Menu Objcect
  // ----------------------------
  updateMenuItemObj(data: MenuItem, orderTypeId: number): POMenuItem {
    return {
      menu_id: data.id,
      menu_name: data.name,
      quantity: data.quantity ?? 1,
      price: this.changePriceBaseOnOrderType(data, orderTypeId, ''),
      take_away_price: data.take_away_price,
      delivery_price: data.delivery_price,
      dinin_price: data.price,
      menu_variation: null,
      order_detail_choice: [],
      note: '',
      stock: parseInt(data.stock || '0'),
    };
  }

  //Update Menu Variation Objcect
  // ----------------------------
  createMenuVariationObj(
    data: MenuVariations,
    orderTypeId: number
  ): POMenuVariations {
    return {
      id: data.id,
      name: data.name,
      price: this.changePriceBaseOnOrderType(data, orderTypeId, ''),
      take_away_price: data.take_away_price,
      delivery_price: data.delivery_price,
      dinin_price: data.price,
    };
  }

  //Update Menu Choice Objcect
  // ----------------------------

  createChoiceObj(
    choice_grp: ChoiceGroups,
    data: Choices,
    orderTypeId: number
  ): POChoices {
    return {
      choice_id: data.id,
      choice_name: data.name,
      price: this.changePriceBaseOnOrderType(data, orderTypeId, ''),
      delivery_price: data.delivery_price,
      take_away_price: data.take_away_price,
      dinin_price: data.price,
      choice_group_id: choice_grp.id,
    };
  }

  //Check Increace Qyt Or Push New Item in Object
  // ----------------------------

  isItemExistInMenu(addedMenuItem: POMenuItem[], newItem: POMenuItem) {
    let isExist: number = -1;
    if (addedMenuItem.length < 1) {
      return isExist;
    }

    if (addedMenuItem && addedMenuItem.length > 0) {
      for (const [index, item] of addedMenuItem.entries()) {
        if (item.isEdit) return isExist;

        if (item.menu_id === newItem.menu_id) {
          if (newItem.menu_variation && item.menu_variation) {
            let isMenuVariationMatch = this.matchMenuVariation(
              item.menu_variation,
              newItem.menu_variation
            );
            if (isMenuVariationMatch) {
              if (
                item.order_detail_choice.length > 0 &&
                newItem.order_detail_choice.length > 0
              ) {
                let isItemChoiceMatch: boolean = this.matchMenuChoices(
                  item.order_detail_choice,
                  newItem.order_detail_choice
                );
                if (isItemChoiceMatch) {
                  isExist = index;
                }
              } else if (
                item.order_detail_choice.length === 0 &&
                newItem.order_detail_choice.length === 0
              ) {
                isExist = index;
              }
            }
          } else {
            if (
              item.order_detail_choice.length > 0 &&
              newItem.order_detail_choice.length > 0
            ) {
              let isItemChoiceMatch: boolean = this.matchMenuChoices(
                item.order_detail_choice,
                newItem.order_detail_choice
              );
              if (isItemChoiceMatch) {
                isExist = index;
              }
            } else if (
              item.order_detail_choice.length === 0 &&
              newItem.order_detail_choice.length === 0
            ) {
              isExist = index;
            }
          }
        }
      }
    }
    return isExist;
  }

  matchMenuVariation(
    menuVariation: POMenuVariations,
    newItemVar: POMenuVariations
  ) {
    let isMatch: boolean = false;
    if (menuVariation.id === newItemVar.id) {
      isMatch = true;
    }
    return isMatch;
  }

  matchMenuChoices(
    itemChoices: POChoices[],
    newItemChoice: POChoices[]
  ): boolean {
    const choiceIdsArray1 = new Set(
      itemChoices.map((choice) => choice.choice_id)
    );
    const choiceIdsArray2 = new Set(
      newItemChoice.map((choice) => choice.choice_id)
    );
    if (choiceIdsArray1.size !== choiceIdsArray2.size) {
      return false;
    }
    for (let choiceId of choiceIdsArray1) {
      if (!choiceIdsArray2.has(choiceId)) {
        return false;
      }
    }

    return true;
  }

  //Calculate Price Based On orderType
  // ----------------------------
  // if you want to Change Value Of PlaceOrder Interfaces Price Pass ("placeOrderObj") Param in Object
  changePriceBaseOnOrderType(
    data: any,
    orderTypeId: number,
    callFrom: string
  ): string {
    if (callFrom === 'placeOrderObj') {
      switch (orderTypeId) {
        case 3:
          return parseFloat(data.delivery_price) >= 0
            ? data.delivery_price
            : data.price;
        case 2:
          return parseFloat(data.take_away_price) >= 0
            ? data.take_away_price
            : data.price;
        case 1:
          return parseFloat(data.dinin_price) >= 0
            ? data.dinin_price
            : data.price;

        default:
          return data.price;
      }
    } else {
      switch (orderTypeId) {
        case 3:
          return parseFloat(data.delivery_price) >= 0
            ? data.delivery_price
            : data.price;
        case 2:
          return parseFloat(data.take_away_price) >= 0
            ? data.take_away_price
            : data.price;

        case 1:
          return data.price ? data.price : '0.00';

        default:
          return data.price;
      }
    }
  }
}
